import { DomainPreferences } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  KpiBenchmarkConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiV2ComponentConfig,
  KpiV2Type,
  LanguageString,
} from '../../../../common/components/dashboards/types';
import { Languages } from '../../../../constants';
import {
  CalendarYearSingleValueByMonthsTimeSelection,
  DataTypes,
  EmployeeDataFields,
  FinancialYearSingleValueByYearsTimeSelection,
  SingleValueTimeSelection,
  TimeSelection,
  TimeSelectionType,
} from '../../../../types';
import { StoryboardDateUtils } from '../../utils';

type SingleMetricFactboxConfig = {
  metricId: RegularMetricId;
  title: LanguageString;
  footer?: LanguageString;
  timeSelection: SingleValueTimeSelection;
  segmentationDataField?: EmployeeDataFields;
  benchmarkConfigs?: KpiBenchmarkConfig[];
  chartGridConfig: ChartGridConfig;
};

const getMonthlyTimeSelection = (
  storyboardDateUtils: StoryboardDateUtils
): CalendarYearSingleValueByMonthsTimeSelection => {
  const { lastMonthEndOfMonth } = storyboardDateUtils;
  return {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: { start: lastMonthEndOfMonth, end: lastMonthEndOfMonth },
  };
};

const getLastFinYearTimeSelection = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): FinancialYearSingleValueByYearsTimeSelection => {
  const { fiscalYear } = storyboardDateUtils;
  return {
    type: TimeSelectionType.FinancialYearSingleValueByYears,
    input: { start: fiscalYear(domainPreferences) - 1, end: fiscalYear(domainPreferences) - 1 },
  };
};

const getSingleMetricFactboxComponentConfig = (config: SingleMetricFactboxConfig): KpiV2ComponentConfig => {
  const { metricId, title, segmentationDataField, benchmarkConfigs, chartGridConfig, footer, timeSelection } = config;
  return {
    chartId: `${metricId}-factbox`,
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title,
    footer,
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      section: {
        key: metricId,
        query: {
          metrics: [{ type: 'RegularMetricId', value: metricId }],
          timeSelection,
          segmentations: segmentationDataField
            ? [
                {
                  type: 'non-hierarchical',
                  dataField: {
                    dataType: DataTypes.EMPLOYEE,
                    dataField: segmentationDataField,
                  },
                },
              ]
            : undefined,
        },
        benchmarkConfigs,
      },
    },
    ...chartGridConfig,
  };
};

export const getIndividualsFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0001_1IndividualsCount,
    title: {
      [Languages.EN]: '従業員数',
      [Languages.JA]: '従業員数',
    },
    footer: {
      [Languages.EN]: '対象期間末時点の総数',
      [Languages.JA]: '対象期間末時点の総数',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    segmentationDataField: EmployeeDataFields.FUNCTION_LEVEL_1,
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getAvgAgeFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0019_2AgeAvg,
    title: {
      [Languages.EN]: '平均年齢',
      [Languages.JA]: '平均年齢',
    },
    footer: {
      [Languages.EN]: '対象期間末時点の平均',
      [Languages.JA]: '対象期間末時点の平均',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getAvgTenureFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0023_2TenureAvg,
    title: {
      [Languages.EN]: '平均勤続年数',
      [Languages.JA]: '平均勤続年数',
    },
    footer: {
      [Languages.EN]: '対象期間末時点の平均',
      [Languages.JA]: '対象期間末時点の平均',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getPercFemaleManagerFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0012_3FMngCalcPercMng,
    title: {
      [Languages.EN]: '女性管理職比率',
      [Languages.JA]: '女性管理職比率',
    },
    footer: {
      [Languages.EN]: `目標値：20%以上
対象期間末時点の比率`,
      [Languages.JA]: `目標値：20%以上
対象期間末時点の比率`,
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getJoinersFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0006_1JoinersJoiners,
    title: {
      [Languages.EN]: '入社者',
      [Languages.JA]: '入社者',
    },
    footer: {
      [Languages.EN]: '対象期間における累計',
      [Languages.JA]: '対象期間における累計',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getLeaversFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
    title: {
      [Languages.EN]: '離職者',
      [Languages.JA]: '離職者',
    },
    footer: {
      [Languages.EN]: '対象期間における累計',
      [Languages.JA]: '対象期間における累計',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getAttritionTTMFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0076_3AttritionTtm,
    title: {
      [Languages.EN]: '離職率',
      [Languages.JA]: '離職率',
    },
    footer: {
      [Languages.EN]: '対象期間末時点の比率',
      [Languages.JA]: '対象期間末時点の比率',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getMonthlyOtFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0215_1MonthlyOtSumPerHc,
    title: {
      [Languages.EN]: '時間外労働（従業員一人当たり平均）',
      [Languages.JA]: '時間外労働（従業員一人当たり平均）',
    },
    footer: {
      [Languages.EN]: '対象期間末時点の平均',
      [Languages.JA]: '対象期間末時点の平均',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getCustomSurveyFactboxConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0413_1CstmSurveyMixiPositiveRatio,
    title: {
      [Languages.EN]: 'エンゲージメントスコア',
      [Languages.JA]: 'エンゲージメントスコア',
    },
    footer: {
      [Languages.EN]: `目標値：75%以上
直近のサーベイ実施時スコア`,
      [Languages.JA]: `目標値：75%以上
直近のサーベイ実施時スコア`,
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getLastFinYearTimeSelection(storyboardDateUtils, domainPreferences),
  });
};

export const getInternalMobilityFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  return getSingleMetricFactboxComponentConfig({
    metricId: RegularMetricId.Pm0033_2InternalMobilityOrgLevel1NumIndvWMove,
    title: {
      [Languages.EN]: '社内異動数',
      [Languages.JA]: '社内異動数',
    },
    footer: {
      [Languages.EN]: '対象期間末時点の平均',
      [Languages.JA]: '対象期間末時点の平均',
    },
    chartGridConfig: {
      gridRowSpan: 1,
      gridColSpan: 5,
    },
    benchmarkConfigs: [
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
      {
        benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
        benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
      },
    ],
    timeSelection: getMonthlyTimeSelection(storyboardDateUtils),
  });
};

export const getGenderRatioFactboxConfig = (storyboardDateUtils: StoryboardDateUtils): KpiV2ComponentConfig => {
  const { lastMonthEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: lastMonthEndOfMonth,
      end: lastMonthEndOfMonth,
    },
  };
  return {
    chartId: 'gender-ratio-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: '男女比（女：男）',
      [Languages.JA]: '男女比（女：男）',
    },
    footer: {
      [Languages.EN]: '対象期間末時点の比率',
      [Languages.JA]: '対象期間末時点の比率',
    },
    options: {
      kpiType: KpiV2Type.DOUBLE_METRIC,
      sections: {
        leftSection: {
          key: 'female-ratio',
          title: {
            [Languages.EN]: '女性',
            [Languages.JA]: '女性',
          },
          query: {
            metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0020_2FhcPercHc }],
            timeSelection,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
            },
          ],
        },
        rightSection: {
          key: 'male-ratio',
          title: {
            [Languages.EN]: '男性',
            [Languages.JA]: '男性',
          },
          query: {
            metrics: [{ type: 'RegularMetricId', value: RegularMetricId.Pm0448_2MhcPerPerson }],
            timeSelection,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
              benchmarkType: KpiFactboxBenchmarkTypes.VALUE,
            },
          ],
        },
      },
    },
    gridRowSpan: 1,
    gridColSpan: 5,
  };
};
